import { useEffect, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import type { IconType } from 'react-icons'
import {
  FaArrowLeft,
  FaArrowRotateLeft,
  FaBed,
  FaBus,
  FaCheck,
  FaClock,
  FaHeadset,
  FaLocationDot,
  FaMinus,
  FaPassport,
  FaPlane,
  FaPlus,
  FaShield,
  FaStar,
  FaTicket,
  FaUtensils,
  FaVanShuttle,
  FaXmark
} from 'react-icons/fa6'
import { resolvePackageIcon } from '../components/DestinationIdComp/packageIconRegistry'
import {
  DEFAULT_PACKAGES_ROUTE_ID,
  getPackageListRouteGroupById,
  getUnifiedPackageDetailById
} from '../utils/packageEnquiry'
import { cmsApi } from '../services/cms'

type TabKey = 'overview' | 'itinerary' | 'inclusions' | 'visa' | 'reviews'

const tabs: TabKey[] = [
  'overview',
  'itinerary',
  'inclusions',
  'visa',
  'reviews'
]
const tabLabels: Record<TabKey, string> = {
  overview: 'Overview',
  itinerary: 'Itinerary',
  inclusions: 'Inclusions & Exclusions',
  visa: 'Entry & Policies',
  reviews: 'Reviews '
}

const heroImageByRoute: Record<string, string> = {
  'maldives-ultra-luxury':
    'https://images.unsplash.com/photo-1573843981267-be1999ff37cd?auto=format&fit=crop&w=2200&q=80',
  'maldives-luxury':
    'https://images.unsplash.com/photo-1519046904884-53103b34b206?auto=format&fit=crop&w=2200&q=80',
  'maldives-premium':
    'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=2200&q=80',
  'maldives-budget':
    'https://images.unsplash.com/photo-1540541338287-41700207dee6?auto=format&fit=crop&w=2200&q=80',
  'georgia-georgian-escape':
    'https://images.unsplash.com/photo-1513326738677-b964603b136d?auto=format&fit=crop&w=2200&q=80',
  'georgia-enchanting-georgia':
    'https://images.unsplash.com/photo-1565004602747-718e1e4f7de3?auto=format&fit=crop&w=2200&q=80',
  'georgia-best-of-georgia':
    'https://images.unsplash.com/photo-1556306535-38febf6782e7?auto=format&fit=crop&w=2200&q=80',
  'azerbaijan-azerbaijan-getaway':
    '/m_Azerbaijan_destination_landscape_image_1_l_1447_1929.webp',
  'azerbaijan-city-and-nature-escape':
    '/m_Baku_destination_image_1_l_651_1000%20(1).webp',
  'azerbaijan-discover-azerbaijan':
    '/azerbaijani-mosque-flame-towers.webp',
  'kenya-safari-escape':
    'https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=2200&q=80',
  'kenya-wildlife-discovery':
    'https://images.unsplash.com/photo-1503917988258-f87a78e3c995?auto=format&fit=crop&w=2200&q=80',
  'sri-lanka-essential':
    'https://images.unsplash.com/photo-1589394815804-964ed0be2eb5?auto=format&fit=crop&w=2200&q=80',
  'sri-lanka-signature':
    'https://images.unsplash.com/photo-1589394815804-964ed0be2eb5?auto=format&fit=crop&w=2200&q=80',
  'sri-lanka-golden-highlights':
    'https://images.unsplash.com/photo-1566296314736-6eaac1ca0cb9?auto=format&fit=crop&w=2200&q=80',
  'tanzania-safari-collection':
    'https://images.unsplash.com/photo-1523805009345-7448845a9e53?auto=format&fit=crop&w=2200&q=80'
}

const heroImageByPackageId: Record<string, string> = {
  'cheval-blanc-randheli':
    '/destination/Maldives%20Ultra%20Luxury/Cheval%20Blanc%20Randhali%20Maldives.jpg',
  'joali-maldives':
    '/destination/Maldives%20Ultra%20Luxury/Joali%20Maldives.webp',
  'ritz-carlton-maldives':
    '/destination/Maldives%20Ultra%20Luxury/Ritz%20Carlton.jpg',
  'waldorf-astoria-maldives':
    '/destination/Maldives%20Ultra%20Luxury/Waldorf%20Astoria.webp',
  'one-and-only-reethi-rah':
    '/destination/Maldives Luxury/One & Only Reethi Rah.webp',
  'niyama-private-islands':
    '/destination/Maldives%20Luxury/Niyama%20Private%20Island.jpg',
  'four-seasons-kuda-huraa':
    '/destination/Maldives%20Luxury/Four%20Seasons%20Kuda%20Hura.webp',
  'kuda-villingili':
    '/destination/Maldives%20Luxury/Kuda%20Villingilli%20Resort%20Maldives.jpg',
  'fairmont-sirru-fen-fushi':
    '/destination/Maldives%20Luxury/Sirru%20Fen%20Fushi%20%E2%80%93%20Private%20Lagoon%20Resort.webp',
  'centara-grand-lagoon':
    '/destination/Maldives%20Premium/Centara%20Grand%20Lagoon%20Maldives.webp',
  'hilton-amingiri':
    '/destination/Maldives%20Premium/Hilton%20Maldives%20Amingiri.jpg',
  'six-senses-laamu':
    '/destination/Maldives%20Premium/Six%20Senses%20Laamu.jpg',
  'constance-halaveli':
    '/destination/Maldives%20Premium/Constance%20Halaveli.webp',
  'anantara-veli':
    '/destination/Maldives%20Premium/Anantara%20Veli%20Maldives%20Resort.png',
  'alila-kothaifaru': '/destination/Maldives%20Premium/Alila%20Kothaifaru.webp',
  'sheraton-full-moon':
    '/destination/Maldives Budget/Sheraton Maldives Maldives Full Moon Resort & Spa.webp',
  'medhufushi-island-resort':
    '/destination/Maldives%20Budget/Medhufushi%20Island%20Resort.jpeg',
  'hard-rock-maldives':
    '/destination/Maldives%20Budget/Hard%20Rock%20Maldives.jpg',
  'cinnamon-dhonveli': '/destination/Maldives%20Budget/Cinnamon%20Dhonveli.jpg',
  'nh-kuda-rah':
    '/destination/Maldives%20Budget/NH%20Maldives%20Kuda%20Rah%20Resort.jpg',
  'coco-bodu-hithi': '/destination/Maldives%20Budget/Coco%20Bodu%20Hithi.avif',
  'kurumba-maldives': '/destination/Maldives%20Budget/Kurumba%20Maldives.webp',
  'saii-lagoon-maldives': '/destination/Maldives%20Budget/Saii%20Lagoon.avif',
  'velassaru-maldives':
    '/destination/Maldives%20Budget/Velassaru%20Maldives.jpg',
  'kenya-safari-escape-mid-range':
    'https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=2200&q=80',
  'kenya-safari-escape-luxury':
    'https://images.unsplash.com/photo-1503917988258-f87a78e3c995?auto=format&fit=crop&w=2200&q=80',
  'kenya-wildlife-discovery-mid-range':
    'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=2200&q=80',
  'kenya-wildlife-discovery-luxury':
    'https://images.unsplash.com/photo-1549366021-9f761d450615?auto=format&fit=crop&w=2200&q=80',
  'sri-lanka-signature':
    'https://images.unsplash.com/photo-1589394815804-964ed0be2eb5?auto=format&fit=crop&w=2200&q=80',
  'sri-lanka-golden-highlights':
    'https://images.unsplash.com/photo-1566296314736-6eaac1ca0cb9?auto=format&fit=crop&w=2200&q=80',
  'sri-lanka-essence':
    'https://images.unsplash.com/photo-1544737151-6e4b8ca3f4f7?auto=format&fit=crop&w=2200&q=80',
  'tanzania-wildlife-explorer':
    'https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=2200&q=80',
  'tanzania-classic-safari':
    'https://images.unsplash.com/photo-1523805009345-7448845a9e53?auto=format&fit=crop&w=2200&q=80',
  'tanzania-ultimate-safari':
    'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=2200&q=80',
  'georgia-best-of-georgia-5-star':
    '/m_Georgia.webp'
}

type CmsMainPackage = {
  id: string
  title?: string | null
  destination?: string | null
  description?: string | null
  websiteSlug?: string | null
}

type CmsFeature = {
  iconName?: string | null
  title?: string | null
  description?: string | null
}

type CmsItinerary = {
  day?: number | string | null
  title?: string | null
  description?: string | null
  features?: Array<CmsFeature | string> | null
}

type CmsSubPackage = {
  id?: string | null
  packageId?: string | null
  name?: string | null
  title?: string | null
  destination?: string | null
  location?: string | null
  duration?: string | null
  durationDays?: number | null
  durationNights?: number | null
  transport?: string | null
  startingPrice?: number | null
  startingPriceCurrency?: string | null
  rating?: number | null
  image?: string | null
  bannerImageUrl?: string | null
  galleryImageUrls?: string[] | null
  description?: string | null
  snapshot?: string | null
  features?: CmsFeature[] | null
  itineraries?: CmsItinerary[] | null
  highlights?: string[] | null
  inclusions?: string[] | null
  exclusions?: string[] | null
  paymentTerms?: string[] | null
  cancellationPolicy?: string[] | null
  tnc?: string[] | null
  impNotes?: string[] | null
  status?: string | null
  packageCategory?: string | null
  isSoldOut?: boolean | null
}

type PackageDetailViewModel = {
  id: string
  destinationId: string
  destinationName: string
  title: string
  collectionTitle: string
  collectionRouteId: string
  location: string
  duration: string
  transfer: string
  startingPrice: number
  priceCurrency: string
  rating: number
  reviews: number
  tag: string
  summary: string
  destinationOverview: string
  highlights: string[]
  features?: Array<{
    iconName?: string
    title: string
    description: string
  }>
  itinerary: Array<{
    day: string
    title: string
    details: string
    stay?: string
    meals?: string
    featureDescriptions?: string[]
  }>
  inclusions: string[]
  exclusions: string[]
  paymentTerms: string[]
  cancellationPolicy: string[]
  termsAndConditions: string[]
  importantNotes: string[]
  image: string
  galleryImageUrls: string[]
}

const siteRegion = (import.meta.env.VITE_SITE_REGION || 'IN').toUpperCase()
const siteCountry = siteRegion === 'AE' ? 'United Arab Emirates' : 'India'
const defaultCurrency = siteRegion === 'AE' ? 'AED' : 'INR'
const PACKAGE_CONTEXT_STORAGE_KEY = 'g2v_package_context_v1'
const uuidRegex =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const normalizeToken = (value?: string | null) =>
  (value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

const toSlug = (value?: string | null) =>
  (value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')

const toDuration = (
  duration?: string | null,
  durationDays?: number | null,
  durationNights?: number | null
) => {
  if (duration?.trim()) return duration.trim()

  if (
    typeof durationDays === 'number' &&
    Number.isFinite(durationDays) &&
    typeof durationNights === 'number' &&
    Number.isFinite(durationNights)
  ) {
    return `${durationDays}D/${durationNights}N`
  }

  return 'Custom Duration'
}

const toDestinationId = (value?: string | null) => {
  const normalized = normalizeToken(value)
  if (!normalized) return 'other'
  if (normalized.includes('maldives')) return 'maldives'
  if (normalized.includes('georgia')) return 'georgia'
  if (normalized.includes('azerbaijan')) return 'azerbaijan'
  if (normalized.includes('sri lanka')) return 'sri-lanka'
  if (normalized.includes('kenya')) return 'kenya'
  if (normalized.includes('tanzania')) return 'tanzania'
  if (normalized.includes('japan')) return 'japan'
  return toSlug(value) || 'other'
}

const toDestinationName = (destinationId: string) => {
  if (destinationId === 'sri-lanka') return 'Sri Lanka'
  if (destinationId === 'maldives') return 'Maldives'
  if (destinationId === 'georgia') return 'Georgia'
  if (destinationId === 'azerbaijan') return 'Azerbaijan'
  if (destinationId === 'kenya') return 'Kenya'
  if (destinationId === 'tanzania') return 'Tanzania'
  if (destinationId === 'japan') return 'Japan'
  return 'Destination'
}

const resolveRouteFromDestination = (destinationId: string) => {
  if (destinationId === 'kenya') return 'kenya-safari-collection'
  if (destinationId === 'tanzania') return 'tanzania-safari-collection'
  if (destinationId === 'georgia') return 'georgia-georgian-escape'
  if (destinationId === 'azerbaijan') return 'azerbaijan-azerbaijan-getaway'
  if (destinationId === 'sri-lanka') return 'sri-lanka-essential'
  return DEFAULT_PACKAGES_ROUTE_ID
}

const toViewModelFromStatic = (
  detail: NonNullable<ReturnType<typeof getUnifiedPackageDetailById>>
): PackageDetailViewModel => ({
  id: detail.id,
  destinationId: detail.destinationId,
  destinationName: toDestinationName(detail.destinationId),
  title: detail.title,
  collectionTitle: detail.collectionTitle,
  collectionRouteId: detail.collectionRouteId,
  location: detail.location,
  duration: detail.duration,
  transfer: detail.transfer,
  startingPrice: detail.startingPricePerPersonAed,
  priceCurrency: defaultCurrency,
  rating: detail.rating,
  reviews: detail.reviews,
  tag: detail.tag,
  summary: detail.summary,
  destinationOverview: detail.destinationOverview,
  highlights: detail.highlights,
  features: undefined,
  itinerary: detail.itinerary,
  inclusions: detail.inclusions,
  exclusions: detail.exclusions,
  paymentTerms: detail.paymentTerms,
  cancellationPolicy: detail.cancellationPolicy,
  termsAndConditions: detail.termsAndConditions,
  importantNotes: detail.importantNotes,
  image: '',
  galleryImageUrls: []
})

const toViewModelFromCms = (
  subPackage: CmsSubPackage,
  options: {
    mainPackage?: CmsMainPackage
    routeIdHint?: string
  }
): PackageDetailViewModel | null => {
  const packageId = (subPackage.packageId || subPackage.id || '').trim()
  if (!packageId) {
    return null
  }

  const destinationName =
    subPackage.destination?.trim() ||
    options.mainPackage?.destination?.trim() ||
    'Destination'
  const destinationId = toDestinationId(destinationName)
  const fallbackRouteId =
    options.routeIdHint ||
    options.mainPackage?.websiteSlug?.trim() ||
    options.mainPackage?.id ||
    resolveRouteFromDestination(destinationId)
  const routeGroupTitle = getPackageListRouteGroupById(fallbackRouteId)?.title

  const featureDescriptions = (subPackage.features || [])
    .map(feature => feature.description?.trim() || feature.title?.trim() || '')
    .filter(Boolean)

  const transferFromFeature = featureDescriptions.find(item =>
    /(transfer|speedboat|seaplane|yacht|transport)/i.test(item)
  )

  const itinerary = (subPackage.itineraries || [])
    .map((item, index) => {
      const dayValue =
        typeof item.day === 'number' || typeof item.day === 'string'
          ? String(item.day).trim()
          : `${index + 1}`

      const itineraryFeatures = (item.features || [])
        .map(feature => {
          if (typeof feature === 'string') {
            return feature.trim()
          }
          return feature.description?.trim() || feature.title?.trim() || ''
        })
        .filter(Boolean)
      const stay =
        itineraryFeatures.find(feature =>
          /(hotel|resort|stay|villa)/i.test(feature)
        ) || undefined
      const meals =
        itineraryFeatures.find(feature =>
          /(meal|breakfast|lunch|dinner|board)/i.test(feature)
        ) || undefined

      return {
        day: dayValue ? `Day ${dayValue}` : `Day ${index + 1}`,
        title: item.title?.trim() || 'Planned Activities',
        details:
          item.description?.trim() ||
          'Detailed itinerary will be shared after your enquiry.',
        stay,
        meals,
        featureDescriptions: itineraryFeatures
      }
    })

  const mergedImages = [
    subPackage.bannerImageUrl,
    subPackage.image,
    ...(subPackage.galleryImageUrls || [])
  ].filter((value): value is string => Boolean(value?.trim()))

  const galleryImageUrls = Array.from(new Set(mergedImages))
  const primaryImage = galleryImageUrls[0] || ''
  const rating =
    typeof subPackage.rating === 'number' && Number.isFinite(subPackage.rating)
      ? Number(subPackage.rating.toFixed(1))
      : 4.8

  const highlights =
    Array.isArray(subPackage.highlights) && subPackage.highlights.length
      ? subPackage.highlights
      : featureDescriptions

  const features = (subPackage.features || [])
    .map(feature => ({
      iconName: feature.iconName?.trim() || undefined,
      title: feature.title?.trim() || '',
      description: feature.description?.trim() || ''
    }))
    .filter(feature => feature.title || feature.description)
    .map(feature => ({
      iconName: feature.iconName,
      title: feature.title || feature.description,
      description: feature.description || feature.title
    }))

  return {
    id: packageId,
    destinationId,
    destinationName,
    title: subPackage.title?.trim() || subPackage.name?.trim() || 'Package',
    collectionTitle:
      routeGroupTitle ||
      options.mainPackage?.title?.trim() ||
      subPackage.destination?.trim() ||
      'Curated Package',
    collectionRouteId: fallbackRouteId,
    location: subPackage.location?.trim() || destinationName,
    duration: toDuration(
      subPackage.duration,
      subPackage.durationDays,
      subPackage.durationNights
    ),
    transfer: subPackage.transport?.trim() || transferFromFeature || 'Private transfer',
    startingPrice:
      typeof subPackage.startingPrice === 'number' &&
      Number.isFinite(subPackage.startingPrice)
        ? subPackage.startingPrice
        : 0,
    priceCurrency: (subPackage.startingPriceCurrency || defaultCurrency).toUpperCase(),
    rating,
    reviews: Math.max(12, Math.round(rating * 18)),
    tag: subPackage.isSoldOut
      ? 'Sold Out'
      : subPackage.status?.toUpperCase() === 'DRAFT'
        ? 'Draft'
        : 'Featured',
    summary:
      subPackage.snapshot?.trim() ||
      subPackage.description?.trim() ||
      'Package summary will be shared on enquiry.',
    destinationOverview:
      subPackage.description?.trim() ||
      subPackage.snapshot?.trim() ||
      'Destination overview will be shared on enquiry.',
    highlights: highlights.slice(0, 8),
    features,
    itinerary:
      itinerary.length > 0
        ? itinerary
        : [
            {
              day: 'Day 1',
              title: 'Arrival and Leisure',
              details: 'Detailed itinerary will be shared after your enquiry.'
            }
          ],
    inclusions: (subPackage.inclusions || []).filter(Boolean),
    exclusions: (subPackage.exclusions || []).filter(Boolean),
    paymentTerms: (subPackage.paymentTerms || []).filter(Boolean),
    cancellationPolicy: (subPackage.cancellationPolicy || []).filter(Boolean),
    termsAndConditions: (subPackage.tnc || []).filter(Boolean),
    importantNotes: (subPackage.impNotes || []).filter(Boolean),
    image: primaryImage,
    galleryImageUrls
  }
}

const readStoredPackageContext = (packageId?: string | null) => {
  if (typeof window === 'undefined' || !packageId) return null

  try {
    const existingRaw = window.sessionStorage.getItem(PACKAGE_CONTEXT_STORAGE_KEY)
    if (!existingRaw) return null

    const existing = JSON.parse(existingRaw) as Record<
      string,
      { mainPackageId: string; routeId: string }
    >
    return existing[packageId] || null
  } catch {
    return null
  }
}

const rememberPackageContextById = (
  packageId: string,
  mainPackageId: string,
  routeId: string
) => {
  if (typeof window === 'undefined' || !packageId || !mainPackageId) return

  try {
    const existingRaw = window.sessionStorage.getItem(PACKAGE_CONTEXT_STORAGE_KEY)
    const existing = existingRaw
      ? (JSON.parse(existingRaw) as Record<string, { mainPackageId: string; routeId: string }>)
      : {}

    existing[packageId] = { mainPackageId, routeId }
    window.sessionStorage.setItem(
      PACKAGE_CONTEXT_STORAGE_KEY,
      JSON.stringify(existing)
    )
  } catch {
    // Ignore storage failures.
  }
}

function PackageDetails () {
  const { id } = useParams()
  const location = useLocation()
  const staticDetailRaw = id ? getUnifiedPackageDetailById(id) : undefined
  const staticDetail = staticDetailRaw
    ? toViewModelFromStatic(staticDetailRaw)
    : null
  const [cmsDetail, setCmsDetail] = useState<PackageDetailViewModel | null>(null)
  const [isCmsLoading, setIsCmsLoading] = useState(false)
  const [isCmsResolved, setIsCmsResolved] = useState(false)
  const [activeTab, setActiveTab] = useState<TabKey>('overview')
  const [travelers, setTravelers] = useState(2)
  const [date, setDate] = useState('2026-10-15')

  const searchParams = new URLSearchParams(location.search)
  const mainPackageIdFromQuery = searchParams.get('mainPackageId')?.trim() || ''
  const routeIdFromQuery = searchParams.get('routeId')?.trim() || ''
  const hasStaticDetail = Boolean(staticDetailRaw)
  const shouldTryCms =
    Boolean(id) &&
    (!hasStaticDetail || Boolean(mainPackageIdFromQuery) || uuidRegex.test(id || ''))

  useEffect(() => {
    let cancelled = false

    const loadCmsDetail = async () => {
      setIsCmsResolved(false)

      if (!id) {
        setCmsDetail(null)
        setIsCmsLoading(false)
        setIsCmsResolved(true)
        return
      }

      if (!shouldTryCms) {
        setCmsDetail(null)
        setIsCmsLoading(false)
        setIsCmsResolved(true)
        return
      }

      setIsCmsLoading(true)
      setCmsDetail(null)

      try {
        const mainPackagesResponse = await cmsApi.getMainPackages(siteCountry)
        const mainPackages = Array.isArray(mainPackagesResponse?.data)
          ? (mainPackagesResponse.data as CmsMainPackage[])
          : []
        const resolvedMainPackageIds = new Set<string>()

        const resolveFromMainPackage = async (
          mainPackageId: string,
          routeIdHint?: string,
          allowFirstSubPackage = false
        ) => {
          if (!mainPackageId || resolvedMainPackageIds.has(mainPackageId)) {
            return null
          }

          resolvedMainPackageIds.add(mainPackageId)

          const subPackagesResponse = await cmsApi.getSubPackages(
            mainPackageId,
            siteCountry
          )
          const subPackages = Array.isArray(subPackagesResponse?.data)
            ? (subPackagesResponse.data as CmsSubPackage[])
            : []

          if (!subPackages.length) {
            return null
          }

          const matchedSubPackage =
            subPackages.find(subPackage => {
              const subId = (subPackage.packageId || subPackage.id || '').trim()
              return subId === id
            }) || (allowFirstSubPackage ? subPackages[0] : undefined)

          if (!matchedSubPackage) {
            return null
          }

          const mainPackage = mainPackages.find(item => item.id === mainPackageId)
          const detailViewModel = toViewModelFromCms(matchedSubPackage, {
            mainPackage,
            routeIdHint:
              routeIdHint ||
              mainPackage?.websiteSlug?.trim() ||
              mainPackage?.id ||
              resolveRouteFromDestination(
                toDestinationId(mainPackage?.destination || matchedSubPackage.destination)
              )
          })

          if (!detailViewModel) {
            return null
          }

          rememberPackageContextById(
            detailViewModel.id,
            mainPackageId,
            detailViewModel.collectionRouteId
          )
          if (id !== detailViewModel.id) {
            rememberPackageContextById(
              id,
              mainPackageId,
              detailViewModel.collectionRouteId
            )
          }

          return detailViewModel
        }

        let resolvedDetail: PackageDetailViewModel | null = null

        if (mainPackageIdFromQuery) {
          resolvedDetail = await resolveFromMainPackage(
            mainPackageIdFromQuery,
            routeIdFromQuery
          )
        }

        if (!resolvedDetail) {
          const storedContext = readStoredPackageContext(id)
          if (storedContext?.mainPackageId) {
            resolvedDetail = await resolveFromMainPackage(
              storedContext.mainPackageId,
              storedContext.routeId || routeIdFromQuery
            )
          }
        }

        if (!resolvedDetail) {
          const matchingMainPackage = mainPackages.find(
            mainPackage => mainPackage.id === id
          )

          if (matchingMainPackage?.id) {
            resolvedDetail = await resolveFromMainPackage(
              matchingMainPackage.id,
              routeIdFromQuery ||
                matchingMainPackage.websiteSlug?.trim() ||
                matchingMainPackage.id,
              true
            )
          }
        }

        if (!resolvedDetail) {
          for (const mainPackage of mainPackages) {
            if (!mainPackage.id) continue

            const detailFromLoop = await resolveFromMainPackage(
              mainPackage.id,
              routeIdFromQuery ||
                mainPackage.websiteSlug?.trim() ||
                mainPackage.id
            )

            if (detailFromLoop) {
              resolvedDetail = detailFromLoop
              break
            }
          }
        }

        if (!cancelled) {
          setCmsDetail(resolvedDetail)
        }
      } catch {
        if (!cancelled) {
          setCmsDetail(null)
        }
      } finally {
        if (!cancelled) {
          setIsCmsLoading(false)
          setIsCmsResolved(true)
        }
      }
    }

    loadCmsDetail()

    return () => {
      cancelled = true
    }
  }, [id, mainPackageIdFromQuery, routeIdFromQuery, shouldTryCms])

  const detail = cmsDetail ?? staticDetail

  if (!detail && shouldTryCms && (!isCmsResolved || isCmsLoading)) {
    return (
      <div className='mx-auto max-w-3xl px-4 py-40 text-center'>
        <h1 className='font-heading text-4xl font-bold text-brand-dark'>
          Loading Package...
        </h1>
        <p className='mt-4 text-gray-600'>
          We are preparing your package details.
        </p>
      </div>
    )
  }

  if (!detail) {
    const lowerPackageId = (id || '').toLowerCase()
    let redirectRoute = routeIdFromQuery || DEFAULT_PACKAGES_ROUTE_ID

    if (!routeIdFromQuery) {
      if (lowerPackageId.includes('kenya')) {
        redirectRoute = 'kenya-safari-collection'
      } else if (lowerPackageId.includes('tanzania')) {
        redirectRoute = 'tanzania-safari-collection'
      } else if (lowerPackageId.includes('georgia')) {
        redirectRoute = 'georgia-georgian-escape'
      } else if (lowerPackageId.includes('azerbaijan')) {
        redirectRoute = 'azerbaijan-azerbaijan-getaway'
      } else if (
        lowerPackageId.includes('sri-lanka') ||
        lowerPackageId.includes('srilanka')
      ) {
        redirectRoute = 'sri-lanka-essential'
      }
    }

    window.location.replace(`/packages/${redirectRoute}`)
    return null
  }

  const displayCurrency = detail.priceCurrency || defaultCurrency
  const pricePerPerson = Math.round(detail.startingPrice / 2)
  const startingPriceWithTaxes = pricePerPerson
  const packageSubtotal = travelers * pricePerPerson
  const total = packageSubtotal
  const heroImage =
    detail.image ||
    heroImageByPackageId[detail.id] ||
    heroImageByRoute[detail.collectionRouteId] ||
    'https://storage.googleapis.com/uxpilot-auth.appspot.com/84deee4332-9810b778ee3c9a1e3811.png'

  // Extract inclusion categories from detail.inclusions
  const getFeatureIcon = (
    iconName?: string,
    title?: string,
    description?: string
  ) => {
    const normalizedText = `${title || ''} ${description || ''}`.toLowerCase()

    let resolvedIconName = iconName?.trim()
    if (!resolvedIconName) {
      if (/(flight|airfare|air ticket)/i.test(normalizedText)) {
        resolvedIconName = 'plane'
      } else if (/(hotel|accommodation|resort|stay)/i.test(normalizedText)) {
        resolvedIconName = 'bed'
      } else if (/(meal|breakfast|lunch|dinner|food|board)/i.test(normalizedText)) {
        resolvedIconName = 'utensils'
      } else if (/(transfer|transport|pickup|drop)/i.test(normalizedText)) {
        resolvedIconName = 'shuttle-van'
      } else if (/(tour|sightseeing|excursion)/i.test(normalizedText)) {
        resolvedIconName = 'map-marked-alt'
      } else if (/(insurance|safety|secure)/i.test(normalizedText)) {
        resolvedIconName = 'shield-alt'
      } else if (/(visa|passport)/i.test(normalizedText)) {
        resolvedIconName = 'file'
      } else {
        resolvedIconName = 'check-circle'
      }
    }

    const IconComponent: IconType =
      resolvePackageIcon(resolvedIconName) ||
      resolvePackageIcon('check-circle') ||
      FaCheck
    return <IconComponent />
  }

  const getFeatureCards = () => {
    const cards = (detail.features || [])
      .map(feature => ({
        icon: getFeatureIcon(feature.iconName, feature.title, feature.description),
        title: feature.title?.trim() || '',
        sub: feature.description?.trim() || ''
      }))
      .filter(card => card.title || card.sub)
      .map(card => ({
        icon: card.icon,
        title: card.title || card.sub,
        sub: card.sub || card.title
      }))

    return cards
  }

  const getInclusionCategories = () => {
    const categories: Array<{ icon: React.ReactNode; title: string; sub: string }> = []
    const inclusionsText = detail.inclusions.join(' ').toLowerCase()

    // Check for flights
    if (inclusionsText.includes('flight') || inclusionsText.includes('airfare') || inclusionsText.includes('air ticket')) {
      categories.push({
        icon: <FaPlane />,
        title: 'Flights',
        sub: 'Return international flights'
      })
    }

    // Check for hotel/accommodation
    if (inclusionsText.includes('hotel') || inclusionsText.includes('accommodation') || inclusionsText.includes('resort') || inclusionsText.includes('stay')) {
      categories.push({
        icon: <FaBed />,
        title: detail.destinationId === 'maldives' ? 'Resort Stay' : 'Accommodation',
        sub: detail.collectionTitle
      })
    }

    // Check for meals
    if (inclusionsText.includes('meal') || inclusionsText.includes('breakfast') || inclusionsText.includes('lunch') || inclusionsText.includes('dinner') || inclusionsText.includes('board')) {
      categories.push({
        icon: <FaUtensils />,
        title: 'Meal Plan',
        sub: detail.destinationId === 'maldives' ? 'Half Board Basis' : 'Daily Breakfast'
      })
    }

    // Check for transfers
    if (inclusionsText.includes('transfer') || inclusionsText.includes('transport') || inclusionsText.includes('pickup')) {
      categories.push({
        icon: <FaVanShuttle />,
        title: 'Transfers',
        sub: detail.transfer
      })
    }

    // Check for tours
    if (inclusionsText.includes('tour') || inclusionsText.includes('sightseeing') || inclusionsText.includes('excursion')) {
      categories.push({
        icon: <FaBus />,
        title: 'Tours',
        sub: 'Guided sightseeing'
      })
    }

    // Check for insurance
    if (inclusionsText.includes('insurance')) {
      categories.push({
        icon: <FaShield />,
        title: 'Insurance',
        sub: 'Travel insurance'
      })
    }

    // Check for visa
    if (inclusionsText.includes('visa')) {
      categories.push({
        icon: <FaPassport />,
        title: 'Visa',
        sub: 'Visa assistance'
      })
    }

    // Always add support
    categories.push({
      icon: <FaTicket />,
      title: 'Support',
      sub: '24/7 Assistance'
    })

    return categories
  }

  const overviewCards = getFeatureCards()
  const fallbackOverviewCards = getInclusionCategories()
  const displayOverviewCards =
    overviewCards.length > 0 ? overviewCards : fallbackOverviewCards
  const entryPolicyDestinationName = detail.destinationName

  const reviewCards = [
    {
      name: 'Ahmed Mansoori',
      travelled: 'Travelled in September 2025',
      comment: `The ${detail.title} experience was seamless from airport transfer to hotel check-in and local sightseeing.`
    },
    {
      name: 'Fatima Hashimi',
      travelled: 'Travelled in January 2026',
      comment:
        'The itinerary planning and on-ground support were excellent. Everything was on time and very well managed.'
    },
    {
      name: 'Mohammed Zaabi',
      travelled: 'Travelled in November 2025',
      comment:
        'Highly recommend this package! The team took care of every detail and made our family vacation truly memorable.'
    },
    {
      name: 'Mariam Ketbi',
      travelled: 'Travelled in December 2025',
      comment:
        'Outstanding service from start to finish. The accommodations were luxurious and the tour guides were very knowledgeable.'
    }
  ]

  return (
    <div className='w-full overflow-x-hidden bg-[#f8f9fa]'>
      {/* HERO */}
      <section className='relative flex min-h-[520px] items-end pb-12 pt-24 md:min-h-[640px] md:pb-16 lg:pb-20'>
        <div className='absolute inset-0 z-0'>
          <img
            src={heroImage}
            className='h-full w-full object-cover'
            alt={detail.title}
          />
          <div className='absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent' />
        </div>

        <div className='relative z-10 mx-auto w-full max-w-8xl px-6 md:px-10 xl:px-14'>
          <Link
            to={`/packages/${detail.collectionRouteId}`}
            className='mb-8 inline-flex items-center gap-2 text-sm font-semibold text-white/90 hover:text-white md:mb-44'
          >
            <FaArrowLeft /> Back to {detail.collectionTitle}
          </Link>

          <div className='flex flex-col gap-8 md:flex-row md:items-end md:justify-between'>
            <div className='md:max-w-3xl lg:max-w-4xl'>
              <div className='mb-4 flex items-center gap-3'>
                <span className='rounded-md bg-brand-pink px-3 py-1 text-xs font-bold text-white'>
                  {detail.tag}
                </span>
                <div className='flex items-center rounded-md bg-white/10 px-3 py-1 text-sm text-yellow-400'>
                  <FaStar className='mr-1' /> {detail.rating} ({detail.reviews}{' '}
                  Reviews)
                </div>
              </div>

              <h1 className='mb-4 font-heading text-4xl font-bold text-white md:text-5xl lg:text-6xl'>
                {detail.title}
              </h1>

              <div className='flex flex-wrap gap-6 text-sm text-gray-200'>
                <span className='flex items-center'>
                  <FaLocationDot className='mr-2 text-brand-pink' />{' '}
                  {detail.location}
                </span>
                <span className='flex items-center'>
                  <FaClock className='mr-2 text-brand-pink' /> {detail.duration}
                </span>
                <span className='flex items-center'>
                  <FaPlane className='mr-2 text-brand-pink' /> {detail.transfer}
                </span>
              </div>
            </div>

            <div className='hidden flex-col items-end rounded-2xl bg-black/30 px-6 py-5 text-right backdrop-blur-sm md:flex'>
              <p className='mb-1 text-sm text-gray-300'>Starting from</p>
              <div className='mb-4 text-4xl font-bold text-white'>
                {displayCurrency} {startingPriceWithTaxes.toLocaleString()}{' '}
                <span className='text-lg text-gray-300'>/person</span>
              </div>
              <p className='mb-4 text-sm text-gray-300'>Inclusive of taxes &amp; fees</p>
              <button className='rounded-xl bg-brand-pink px-8 py-4 text-white shadow-lg'>
                Enquire Now -&gt;
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* MAIN */}
      <div className='min-h-screen bg-[#f8f9fa] pb-24 pt-12 md:pt-14'>
        <div className='mx-auto flex max-w-8xl flex-col gap-10 px-6 md:px-10 lg:flex-row xl:gap-12 xl:px-14'>
          {/* LEFT */}
          <div className='order-2 lg:w-2/3 lg:order-1'>
            {/* TABS */}
            <div className='sticky top-24 z-20 -mx-1 mb-10 overflow-x-auto border-b border-gray-200 bg-[#f8f9fa] px-1 pb-2 pt-2'>
              <nav className='flex min-w-max space-x-8 px-1'>
                {tabs.map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`whitespace-nowrap border-b-2 pb-4 text-sm font-semibold transition-colors ${
                      activeTab === tab
                        ? 'border-brand-pink text-brand-pink'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tabLabels[tab]}
                  </button>
                ))}
              </nav>
            </div>

            {/* OVERVIEW TAB */}
            {activeTab === 'overview' && (
              <div className='space-y-8'>
                <div>
                  <h2 className='mb-4 font-heading text-2xl font-bold text-brand-dark'>
                    About This Journey
                  </h2>
                  <p className='mb-4 leading-relaxed text-gray-600'>
                    {detail.destinationOverview}
                  </p>
                  <div className='rounded-2xl border border-brand-pink/15 bg-white p-5 shadow-sm'>
                    <p className='mb-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-pink'>
                      Package Snapshot
                    </p>
                    <p className='leading-relaxed text-gray-600'>
                      {detail.summary}
                    </p>
                  </div>
                </div>

                {detail.galleryImageUrls.length > 0 && (
                  <div>
                    <h3 className='mb-4 font-heading text-xl font-bold text-brand-dark'>
                      Package Gallery
                    </h3>
                    <div className='grid grid-cols-2 gap-4 md:grid-cols-3'>
                      {detail.galleryImageUrls.slice(0, 6).map((imageUrl, index) => (
                        <div
                          key={`${imageUrl}-${index}`}
                          className='h-36 overflow-hidden rounded-xl md:h-40'
                        >
                          <img
                            src={imageUrl}
                            alt={`${detail.title} gallery ${index + 1}`}
                            className='h-full w-full object-cover'
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className='grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4'>
                  {displayOverviewCards.map(item => (
                    <div
                      key={item.title}
                      className='flex flex-col items-center rounded-xl border border-gray-100 bg-white p-4 text-center shadow-sm'
                    >
                      <div className='mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-brand-pink/10 text-xl text-brand-pink'>
                        {item.icon}
                      </div>
                      <h4 className='text-sm font-semibold text-brand-dark'>
                        {item.title}
                      </h4>
                      {/* <p className='mt-1 text-xs text-gray-500'>{item.sub}</p> */}
                    </div>
                  ))}
                </div>

                <div>
                  <h3 className='mb-4 font-heading text-xl font-bold text-brand-dark'>
                    Highlights
                  </h3>
                  <ul className='space-y-3'>
                    {detail.highlights.map(item => (
                      <li key={item} className='flex items-start'>
                        <FaCheck className='mt-1 mr-3 shrink-0 text-brand-pink' />
                        <span className='text-gray-600'>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* ITINERARY TAB */}
            {activeTab === 'itinerary' && (
              <div>
                <h2 className='mb-6 font-heading text-xl font-bold text-brand-dark md:text-2xl'>
                  Day-by-Day Itinerary
                </h2>
                <div className='relative border-l-2 border-gray-200 pl-5 sm:pl-6'>
                  {detail.itinerary.map((item) => (
                    <div key={item.day} className='relative pb-8 pl-5 sm:pl-6'>
                      <div className='absolute -left-[9px] top-1.5 h-4 w-4 rounded-full border-2 border-white bg-brand-pink shadow' />
                      <h3 className='mb-2 text-base font-bold text-brand-dark md:text-lg'>
                        {item.day}: {item.title}
                      </h3>
                      <div className='rounded-xl border border-gray-100 bg-white p-5 shadow-sm'>
                        <p className='mb-4 text-sm text-gray-600'>
                          {item.details}
                        </p>
                        {/* {(item.image || index === 1) && (
                          <div className="mb-4 h-40 w-full overflow-hidden rounded-lg md:h-48">
                            <img
                              src={item.image ?? heroImage}
                              className="h-full w-full object-cover"
                              alt={`${detail.title} itinerary`}
                            />
                          </div>
                        )} */}
                        <div className='flex flex-wrap items-center gap-3 rounded-lg bg-gray-50 p-3 text-xs font-medium text-gray-500'>
                          {(item.featureDescriptions && item.featureDescriptions.length > 0
                            ? item.featureDescriptions
                            : [item.stay ?? detail.title, item.meals ?? 'As per itinerary']
                          )
                            .filter(Boolean)
                            .map((feature, featureIndex) => (
                              <span
                                key={`${item.day}-feature-${featureIndex}`}
                                className='flex items-center gap-1'
                              >
                                <FaCheck className='text-brand-pink' />
                                {feature}
                              </span>
                            ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* INCLUSIONS TAB */}
            {activeTab === 'inclusions' && (
              <div className='grid gap-8 md:grid-cols-2'>
                <div>
                  <h2 className='mb-6 flex items-center gap-3 font-heading text-2xl font-bold text-brand-dark'>
                    <FaCheck className='text-green-500' /> What&apos;s Included
                  </h2>
                  <ul className='space-y-4'>
                    {detail.inclusions.map(item => (
                      <li key={item} className='flex items-start gap-3'>
                        <FaCheck className='mt-1 shrink-0 text-green-500' />
                        <span className='text-sm text-gray-600'>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h2 className='mb-6 flex items-center gap-3 font-heading text-2xl font-bold text-brand-dark'>
                    <FaXmark className='text-red-500' /> What&apos;s Excluded
                  </h2>
                  <ul className='space-y-4'>
                    {detail.exclusions.map(item => (
                      <li key={item} className='flex items-start gap-3'>
                        <FaXmark className='mt-1 shrink-0 text-red-500' />
                        <span className='text-sm text-gray-600'>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* ENTRY & POLICIES TAB */}
            {activeTab === 'visa' && (
              <div className='rounded-2xl border border-gray-100 bg-white p-6 shadow-sm md:p-8'>
                <div className='mb-6 flex items-start gap-4'>
                  <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600'>
                    <FaPassport className='text-xl' />
                  </div>
                  <div>
                    <h2 className='mb-2 font-heading text-xl font-bold text-brand-dark'>
                      {entryPolicyDestinationName} Entry & Travel Policies
                    </h2>
                    <p className='text-sm text-gray-600'>
                      Important travel documentation and booking policies for
                      this package.
                    </p>
                  </div>
                </div>

                <div className='mb-4 grid gap-4 md:grid-cols-2'>
                  <div className='rounded-xl border border-brand-pink/20 bg-brand-pink/5 p-4'>
                    <h4 className='text-sm font-semibold text-brand-dark'>
                      Payment Terms
                    </h4>
                    <ul className='mt-2 space-y-2 text-xs text-gray-600'>
                      {detail.paymentTerms.map(term => (
                        <li key={term} className='flex items-start gap-2'>
                          <FaCheck className='mt-0.5 shrink-0 text-brand-pink' />
                          <span>{term}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className='rounded-xl border border-amber-200 bg-amber-50 p-4'>
                    <h4 className='text-sm font-semibold text-brand-dark'>
                      Cancellation Policy
                    </h4>
                    <ul className='mt-2 space-y-2 text-xs text-gray-600'>
                      {detail.cancellationPolicy.map(policy => (
                        <li key={policy} className='flex items-start gap-2'>
                          <FaCheck className='mt-0.5 shrink-0 text-amber-600' />
                          <span>{policy}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className='mb-4 grid gap-4 md:grid-cols-2'>
                  <div className='rounded-xl border border-blue-100 bg-blue-50 p-4'>
                    <h4 className='text-sm font-semibold text-brand-dark'>
                      Terms & Conditions
                    </h4>
                    <ul className='mt-2 space-y-2 text-xs text-gray-600'>
                      {detail.termsAndConditions.map(term => (
                        <li key={term} className='flex items-start gap-2'>
                          <FaCheck className='mt-0.5 shrink-0 text-blue-600' />
                          <span>{term}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className='rounded-xl border border-gray-200 bg-gray-50 p-4'>
                    <h4 className='text-sm font-semibold text-brand-dark'>
                      Important Notes
                    </h4>
                    <ul className='mt-2 space-y-2 text-xs text-gray-600'>
                      {detail.importantNotes.map(note => (
                        <li key={note} className='flex items-start gap-2'>
                          <FaCheck className='mt-0.5 shrink-0 text-brand-pink' />
                          <span>{note}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* REVIEWS TAB */}
            {activeTab === 'reviews' && (
              <div>
                <div className='mb-8 flex items-center gap-6 border-b border-gray-100 pb-8'>
                  <div className='text-center'>
                    <div className='mb-1 font-heading text-5xl font-bold text-brand-dark'>
                      {detail.rating}
                    </div>
                    <div className='mb-1 flex items-center justify-center gap-1 text-yellow-400 text-sm'>
                      {Array.from({ length: 5 }).map((_, index) => (
                        <FaStar key={`star-${index}`} />
                      ))}
                    </div>
                    <div className='text-xs text-gray-500'>
                      Based on {detail.reviews} reviews
                    </div>
                  </div>
                  <div className='flex-1 space-y-2'>
                    {(
                      [
                        ['5 Stars', '88%'],
                        ['4 Stars', '9%'],
                        ['3 Stars', '3%']
                      ] as [string, string][]
                    ).map(([label, width]) => (
                      <div key={label} className='flex items-center gap-3'>
                        <span className='w-12 text-xs text-gray-500'>
                          {label}
                        </span>
                        <div className='h-2 flex-1 overflow-hidden rounded-full bg-gray-100'>
                          <div
                            className='h-full bg-yellow-400'
                            style={{ width }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className='space-y-6'>
                  {reviewCards.map(review => (
                    <div
                      key={review.name}
                      className='rounded-xl border border-gray-100 bg-white p-6 shadow-sm'
                    >
                      <div className='mb-4 flex items-start justify-between'>
                        <div className='flex items-center gap-3'>
                          <div className='flex h-10 w-10 items-center justify-center rounded-full bg-brand-pink/20 font-bold text-brand-pink'>
                            {review.name.slice(0, 1)}
                          </div>
                          <div>
                            <h4 className='text-sm font-semibold text-brand-dark'>
                              {review.name}
                            </h4>
                            <p className='text-xs text-gray-500'>
                              {review.travelled}
                            </p>
                          </div>
                        </div>
                        <div className='flex items-center gap-1 text-xs text-yellow-400'>
                          {Array.from({ length: 5 }).map((_, index) => (
                            <FaStar key={`${review.name}-star-${index}`} />
                          ))}
                        </div>
                      </div>
                      <p className='text-sm leading-relaxed text-gray-600'>
                        &quot;{review.comment}&quot;
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT SIDEBAR */}
          <div className='order-1 w-full lg:order-2 lg:w-1/3'>
            <div className='relative overflow-hidden rounded-2xl border border-gray-100 bg-white p-5 shadow-xl md:p-6 lg:sticky lg:top-28'>
              <div className='absolute top-0 right-0 z-0 hidden h-24 w-24 rounded-bl-[100px] bg-brand-pink/5 lg:block' />

              <div className='relative z-10'>
                <div className='mb-6 flex items-end justify-between border-b border-gray-100 pb-6'>
                  <div>
                    <p className='mb-1 text-sm text-gray-500'>
                      Starting from
                    </p>
                    <span className='font-heading text-3xl font-bold text-brand-dark'>
                      {displayCurrency} {startingPriceWithTaxes.toLocaleString()}
                    </span>
                    <p className='mt-1 text-xs text-gray-500'>
                      per person
                    </p>
                  </div>
                  <span className='rounded-md bg-green-100 px-2 py-1 text-xs font-bold text-green-700'>
                    Available
                  </span>
                </div>

                <div className='mb-6 space-y-4'>
                  <div>
                    <label className='mb-1.5 block text-sm font-medium text-gray-700'>
                      Select Date
                    </label>
                    <input
                      type='date'
                      value={date}
                      onChange={event => setDate(event.target.value)}
                      className='w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm transition-colors focus:border-brand-pink focus:outline-none focus:ring-1 focus:ring-brand-pink'
                    />
                  </div>
                  <div>
                    <label className='mb-1.5 block text-sm font-medium text-gray-700'>
                      Travelers
                    </label>
                    <div className='flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50 p-2'>
                      <button
                        type='button'
                        onClick={() =>
                          setTravelers(current => Math.max(1, current - 1))
                        }
                        className='flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white transition-colors hover:text-brand-pink'
                      >
                        <FaMinus className='text-sm' />
                      </button>
                      <span className='font-semibold text-brand-dark'>
                        {travelers} Adults
                      </span>
                      <button
                        type='button'
                        onClick={() => setTravelers(current => current + 1)}
                        className='flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white transition-colors hover:text-brand-pink'
                      >
                        <FaPlus className='text-sm' />
                      </button>
                    </div>
                  </div>
                </div>

                <div className='mb-6 space-y-3'>
                  <div className='flex justify-between text-sm text-gray-600'>
                    <span>
                      Package price for {travelers} adults
                    </span>
                    <span className='font-medium text-brand-dark'>
                      {displayCurrency} {packageSubtotal.toLocaleString()}
                    </span>
                  </div>
                  <div className='flex items-center justify-between border-t border-gray-100 pt-3'>
                    <span className='font-semibold text-brand-dark'>Total</span>
                    <span className='text-xl font-bold text-brand-pink'>
                      {displayCurrency} {total.toLocaleString()}
                    </span>
                  </div>
                </div>

                <button className='mb-4 w-full rounded-xl bg-brand-pink py-3 text-white shadow-lg shadow-brand-pink/30 transition-all hover:bg-brand-pink/90 md:py-4 font-semibold'>
                  Enquire Now
                </button>
                <div className='text-center'>
                  <button className='text-sm font-medium text-brand-dark transition-colors hover:text-brand-pink'>
                    Save to Wishlist
                  </button>
                </div>
              </div>
            </div>

            {/* TRUST BADGES */}
            <div className='mt-6 space-y-4'>
              {[
                {
                  icon: <FaShield />,
                  bg: 'bg-green-50',
                  color: 'text-green-500',
                  title: 'Secure Booking',
                  sub: 'Verified partner resorts and secure payments'
                },
                {
                  icon: <FaArrowRotateLeft />,
                  bg: 'bg-blue-50',
                  color: 'text-blue-500',
                  title: 'Flexible Cancellation',
                  sub: 'Charges apply as per supplier cancellation policy'
                },
                {
                  icon: <FaHeadset />,
                  bg: 'bg-purple-50',
                  color: 'text-purple-500',
                  title: '24/7 Support',
                  sub: 'Dedicated travel concierge during your trip'
                }
              ].map(item => (
                <div
                  key={item.title}
                  className='flex items-center gap-3 rounded-xl border border-gray-100 bg-white p-4 shadow-sm'
                >
                  <div
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${item.bg} ${item.color}`}
                  >
                    {item.icon}
                  </div>
                  <div>
                    <h4 className='text-sm font-semibold text-brand-dark'>
                      {item.title}
                    </h4>
                    <p className='text-xs text-gray-500'>{item.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* MOBILE STICKY CTA */}
      <div className='fixed bottom-0 left-0 z-50 flex w-full items-center justify-between border-t border-gray-200 bg-white p-4 shadow-[0_-4px_10px_rgba(0,0,0,0.05)] lg:hidden'>
        <div>
          <p className='text-[10px] font-bold uppercase text-gray-500'>
            Total Price
          </p>
          <div className='text-xl font-bold text-brand-pink'>
            {displayCurrency} {total.toLocaleString()}
          </div>
        </div>
        <button className='rounded-xl bg-brand-pink px-8 py-3 text-sm font-bold text-white shadow-lg shadow-brand-pink/20'>
          Book Now
        </button>
      </div>

      {/* SUPPORT CHIP */}
      <div className='fixed right-6 bottom-24 z-40 lg:bottom-6'>
        <button className='relative flex h-14 w-14 items-center justify-center rounded-full bg-brand-dark text-white shadow-2xl transition-all hover:scale-105 hover:bg-gray-800'>
          <FaHeadset className='text-xl' />
          <span className='absolute top-0 right-0 h-3.5 w-3.5 rounded-full border-2 border-white bg-brand-pink' />
        </button>
      </div>
    </div>
  )
}

export default PackageDetails
